import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { Button } from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import type { AriaRole, CSSProperties, ReactNode } from "react";

const DEFAULT_VIEWPORT_MARGIN_PX = 16;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

interface FloatingWindowAnchor {
  x: number;
  y: number;
}

interface FloatingWindowPositionFromAnchorProps {
  anchor: FloatingWindowAnchor;
  panelHeightPx: number;
  panelWidthPx: number;
  horizontalOffsetPx?: number;
  viewportMarginPx?: number;
  verticalAlign?: "center" | "top";
}

export const getFloatingWindowPositionFromAnchor = ({
  anchor,
  panelHeightPx,
  panelWidthPx,
  horizontalOffsetPx = 0,
  viewportMarginPx = DEFAULT_VIEWPORT_MARGIN_PX,
  verticalAlign = "top",
}: FloatingWindowPositionFromAnchorProps): CSSProperties => {
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const topBase =
    verticalAlign === "center" ? anchor.y - panelHeightPx / 2 : anchor.y;
  const leftBase = anchor.x + horizontalOffsetPx;

  return {
    left: `${clamp(
      leftBase,
      viewportMarginPx,
      viewportWidth - panelWidthPx - viewportMarginPx
    )}px`,
    top: `${clamp(
      topBase,
      viewportMarginPx,
      viewportHeight - panelHeightPx - viewportMarginPx
    )}px`,
  };
};

interface FloatingWindowPositionNearAnchorProps {
  anchor: FloatingWindowAnchor;
  panelHeightPx: number;
  panelWidthPx: number;
  gapPx?: number;
  viewportMarginPx?: number;
}

export const getFloatingWindowPositionNearAnchor = ({
  anchor,
  panelHeightPx,
  panelWidthPx,
  gapPx = 12,
  viewportMarginPx = DEFAULT_VIEWPORT_MARGIN_PX,
}: FloatingWindowPositionNearAnchorProps): CSSProperties => {
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const canPlaceOnRight =
    anchor.x + gapPx + panelWidthPx <= viewportWidth - viewportMarginPx;
  const leftBase = canPlaceOnRight
    ? anchor.x + gapPx
    : anchor.x - gapPx - panelWidthPx;
  const topBase = anchor.y - panelHeightPx / 2;

  return {
    left: `${clamp(
      leftBase,
      viewportMarginPx,
      viewportWidth - panelWidthPx - viewportMarginPx
    )}px`,
    top: `${clamp(
      topBase,
      viewportMarginPx,
      viewportHeight - panelHeightPx - viewportMarginPx
    )}px`,
  };
};

interface FloatingWindowCloseButton {
  onClick: () => void;
  className?: string;
}

interface FloatingWindowRendererProps {
  children: ReactNode;
  blocksHover?: boolean;
  containerClassName: string;
  contentClassName?: string;
  isContextMenu?: boolean;
  panelClassName: string;
  panelRole?: AriaRole;
  positionStyle: CSSProperties;
  closeButton?: FloatingWindowCloseButton;
}

export const FloatingWindowRenderer = ({
  blocksHover = false,
  children,
  closeButton,
  containerClassName,
  contentClassName = "relative min-h-0 flex-1",
  isContextMenu = false,
  panelClassName,
  panelRole,
  positionStyle,
}: FloatingWindowRendererProps) => {
  const handleClick127 = useStableCallback((event) => event.stopPropagation());

  return (
    // Lint exception: Prevents clicks from propagating to the 3D scene
    // Lint exception: No keyboard interaction needed - only stops event propagation
    // Lint exception: Container is not interactive, only catches clicks
    <div
      className={containerClassName}
      data-blocks-hover={blocksHover ? "true" : undefined}
      data-context-menu={isContextMenu ? "" : undefined}
      data-floating-window
      onClick={handleClick127}
      style={positionStyle}
    >
      <div className={panelClassName} role={panelRole}>
        <div className={contentClassName}>
          {children}
          {closeButton && (
            <Button
              appearance="subtle"
              className={closeButton.className}
              icon={<DismissRegular />}
              onClick={closeButton.onClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};
